<script setup>
/**
 * FormSaveCloseBtn
 * Persists form data and navigates away from the form upon success.
 * Handles specialized redirection logic for Site Tree content and
 * ensures query parameters are preserved during the transition.
 */
import {computed} from 'vue'
import {useRoute} from 'vue-router'
import Button from '@/common/Button.vue'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
})

const route = useRoute()
const pageContentDataStore = usePageContentDataStore()
const formStore = useFormStore(props.formId)

// --- Computed ---

const isModified = computed(() => formStore.isModified)

const isSaving = computed(() => formStore.isSaving)

const tableName = computed(() => formStore.tableName || '')

const show = computed(() => {
    return (
        !tableName.value.includes('product_variations__') &&
        tableName.value !== 'files'
    )
})

const isSiteTree = computed(() => route.name === 'siteTree')

/**
 * Calculates the destination URL after a successful save.
 */
const onSaveRedirectUrl = computed(() => {
    if (isSiteTree.value) {
        const path = route.path
        const parts = path.split('/')
        const lastPart = parts.pop() || ''
        // Extract ID from format like 'c123'
        const pageContentId = parseInt(lastPart.substring(1))

        const obj = pageContentDataStore.items.find(o => o.id === pageContentId)
        return obj?.pageId ? `/#/site_tree/${obj.pageId}` : '/#/site_tree'
    }

    let url = `/#/${tableName.value}`
    const search = document.location.hash.split('?')[1]

    if (search) {
        url += `?${search}`
    }

    return url
})

// --- Methods ---

const onClick = () => {
    formStore.save()
        .then(res => {
            // Do not redirect if validation failed
            if (res?.invalid) return

            window.location.href = onSaveRedirectUrl.value
        })
}
</script>

<template>
    <Button
        v-if="show"
        :disabled="isSaving"
        class="rounded-0"
        title="Save and close"
        :active-class="'btn-warning'"
        :active-value="isModified"
        @click="onClick"
    >
        <i class="bi-chevron-right"/>
    </Button>
</template>